import styled from '@emotion/styled';
import { List, Sparkle } from '@phosphor-icons/react';
import { Box, Flex, Text } from '@updater/ui-uds';
import type { KeyboardEvent } from 'react';

interface ChoiceContentProps {
  onChatSelect: () => void;
  onBrowseSelect: () => void;
}

const CardGrid = styled(Flex)`
  gap: 16px;
  width: 100%;
`;

const Card = styled(Box)<{ bgColor: string }>`
  flex: 1;
  min-height: 200px;
  padding: 20px;
  border-radius: 20px;
  background-color: ${({ bgColor }) => bgColor};
  cursor: pointer;
  user-select: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  &:active {
    transform: scale(0.98);
  }

  &:focus-visible {
    outline: 2px solid #581761;
    outline-offset: 2px;
  }
`;

const IconWrapper = styled(Flex)`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background-color: white;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const Heading = styled(Text)`
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: #212121;
`;

const CardTitle = styled(Text)`
  font-size: 20px;
  font-weight: 700;
  line-height: normal;
  color: #212121;
`;

const Subtitle = styled(Text)`
  color: #5d5d5d;
  font-size: 14px;
  line-height: 22px;
  margin-top: 8px;
`;

const FooterText = styled(Text)`
  color: #5d5d5d;
  font-size: 14px;
  line-height: 22px;
  text-align: center;
  margin-top: 20px;
`;

export function ChoiceContent({
  onChatSelect,
  onBrowseSelect,
}: ChoiceContentProps) {
  return (
    <Flex
      flexDirection="column"
      pt="l"
      pb="xl"
      width="100%"
      maxWidth="400px"
      mx="auto"
    >
      <Heading as="h1" mb="m">
        How should we start?
      </Heading>

      <CardGrid>
        <Card
          bgColor="#f2ebff"
          onClick={onChatSelect}
          onKeyDown={(e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onChatSelect();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Chat with AI — Get a personal recommendation"
        >
          <IconWrapper>
            <Sparkle size={24} weight="duotone" color="#581761" />
          </IconWrapper>
          <CardTitle>
            Chat
            <br />
            with AI
          </CardTitle>
          <Subtitle>
            Get a personal
            <br />
            recommendation
          </Subtitle>
        </Card>

        <Card
          bgColor="#e5f3ff"
          onClick={onBrowseSelect}
          onKeyDown={(e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onBrowseSelect();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Browse plans — Compare options side by side"
        >
          <IconWrapper>
            <List size={24} weight="bold" color="#212121" />
          </IconWrapper>
          <CardTitle>
            Browse
            <br />
            plans
          </CardTitle>
          <Subtitle>
            Compare options
            <br />
            side by side
          </Subtitle>
        </Card>
      </CardGrid>

      <FooterText>Not sure? You can always switch later.</FooterText>
    </Flex>
  );
}
